// AppAuthListener.tsx
import { useEffect, useRef } from "react"
import { useDispatch } from "react-redux"
import { message } from "antd"
import { getAuthChannel } from "util/authChannel"
import { userSignInSuccess, userSignOutSuccess } from "appRedux/actions/Auth"
import { systemConfigDataSourceUpdate } from "appRedux/actions/setup/systemSetup/systemConfig"
import { switchLanguage } from "appRedux/actions/setting-actions"
import { isNil } from "lodash"
import { useHistory } from "react-router-dom"
import { useMessage } from "util/messageLocalHook"
import languageData from "./Topbar/languageData"
import { clearAuthStorage } from "util/sessionExpiredHandler"
import { store } from "../NextApp"
import { stateSet } from "constants/StateDefault"

/**
 * Convert language code (e.g., "TH", "EN") to locale object from languageData
 * @param languageCode - Language code from API response (e.g., "TH", "EN", "ZH")
 * @returns Locale object matching languageData structure, or null if not found
 */
const getLocaleFromLanguageCode = (languageCode: string | null | undefined): any => {
    if (!languageCode) return null;
    
    // Normalize language code to lowercase
    const normalizedCode = languageCode.toLowerCase();
    
    // Find matching locale in languageData
    const matchedLocale = languageData.find(
        (lang) => lang.locale.toLowerCase() === normalizedCode
    );
    
    return matchedLocale || null;
};

/** Sync this tab's Redux auth from localStorage (written by another tab). Returns true if synced. */
function syncAuthFromStorage(): boolean {
    if (typeof window === "undefined") return false;
    try {
        const raw = localStorage.getItem(stateSet);
        if (!raw) return false;
        const state = JSON.parse(raw);
        const nextAuthUser = state?.auth?.authUser;
        if (!nextAuthUser) return false;
        const current = store.getState()?.auth?.authUser;
        const sameUser =
            current?.user_id === nextAuthUser?.user_id &&
            (current?.email === nextAuthUser?.email || (!current?.email && !nextAuthUser?.email));
        if (sameUser) return false;
        store.dispatch(userSignInSuccess(nextAuthUser));
        if (nextAuthUser?.system_config) {
            store.dispatch(systemConfigDataSourceUpdate(nextAuthUser.system_config));
        }
        return true;
    } catch {
        return false;
    }
}

export const AppAuthListener = () => {
    const dispatch = useDispatch()
    const history = useHistory()
    const { t } = useMessage()
    const lastSyncRef = useRef(0)

    // Sync this tab when another tab updates auth (storage event) or when user switches back to this tab (visibilitychange)
    useEffect(() => {
        const onStorage = (e: StorageEvent) => {
            if (e.key === stateSet && e.newValue) {
                syncAuthFromStorage();
            }
        };
        const onVisible = () => {
            if (document.visibilityState !== "visible") return;
            const now = Date.now();
            if (now - lastSyncRef.current < 1500) return; // throttle
            lastSyncRef.current = now;
            syncAuthFromStorage();
        };
        window.addEventListener("storage", onStorage);
        document.addEventListener("visibilitychange", onVisible);
        return () => {
            window.removeEventListener("storage", onStorage);
            document.removeEventListener("visibilitychange", onVisible);
        };
    }, []);

    useEffect(() => {
        const bc = getAuthChannel()

        const handler = (event: MessageEvent) => {
            const { type, payload } = event.data || {}

            if (type === "AUTH_CHANGED") {
                const token = payload?.token as string | null
                const data = payload?.userData as any
                const systemConfig = payload?.systemConfig as any
                const deviceId = payload?.deviceId as any

                if (!isNil(token)) {
                    const content = t("app.global.label.AuthBoardcastSignIn")
                    // Sync current tab to latest login (including user switch)
                    localStorage.setItem("access_token", token)
                    if (!isNil(deviceId)) {
                        localStorage.setItem("DEVICE_ID", deviceId)
                    }
                    dispatch(userSignInSuccess(data))
                    dispatch(systemConfigDataSourceUpdate(systemConfig))

                    // Get language from response data and update locale
                    const languageCode = data?.language || data?.user_language || null;
                    const localeObject = getLocaleFromLanguageCode(languageCode);

                    // If locale found, update locale in Redux and localStorage
                    if (localeObject) {
                        localStorage.setItem('locale', JSON.stringify(localeObject));
                        dispatch(switchLanguage(localeObject));
                    }

                    message.info({ content, duration: 1 })

                    const path = history?.location?.pathname || (typeof window !== "undefined" ? window.location.pathname : "")
                    if (path === "/" || path.startsWith("/signin")) {
                        history.replace("/main/dashboard")
                    }
                } else {
                    const content = t("app.global.label.AuthBoardcastSignOut")
                    // Clear auth storage so this tab does not appear logged in after reload
                    clearAuthStorage()
                    dispatch(userSignOutSuccess())
                    message.info({ content, duration: 1 })
                    window.location.replace("/signin")
                }
            } else if (type === "SESSION_EXPIRED") {
                // Another tab detected session expired → logout and redirect immediately (no delay)
                clearAuthStorage()
                dispatch(userSignOutSuccess())
                window.location.replace("/signin")
            }
        }

        bc.addEventListener("message", handler)
        return () => bc.removeEventListener("message", handler)
    }, [dispatch, history, t])

    return null
}

export default AppAuthListener